if (!customElements.get('fnk-sale-pop')) {
  customElements.define('fnk-sale-pop', class extends HTMLElement {
    async connectedCallback() {
      this.hidden = true;
      clearTimeout(this.timer);
      this.controller?.abort();
      this.controller = new AbortController();
      const controller = this.controller;
      const options = { signal: controller.signal };
      this.stopped = false;
      this.hovered = false;
      this.maxSession = Math.max(1, Math.min(5, Number(this.dataset.maxSession) || 3));
      if (Number(this.readStorage('dismissed')) > Date.now()) return;
      if (Number(this.readStorage('shown')) >= this.maxSession) return;
      this.entries = [...this.querySelectorAll('template[data-purchase]')]
        .filter(entry => this.isRecent(entry))
        .sort((a, b) => (Date.parse(b.dataset.at) || 0) - (Date.parse(a.dataset.at) || 0));
      if (['automatic', 'mixed'].includes(this.dataset.mode)) {
        const automatic = await this.loadFeed(controller.signal);
        if (controller.signal.aborted || !this.isConnected) return;
        this.entries = this.dataset.mode === 'automatic' ? automatic : [...automatic, ...this.entries];
      }
      if (!this.entries.length) return;
      this.timeFormat = new Intl.RelativeTimeFormat(this.dataset.locale || 'en', { numeric: 'always' });

      this.shown = 0;
      this.cursor = Number(this.readStorage('cursor')) || 0;
      this.content = this.querySelector('[data-purchase-content]');
      this.querySelector('button').addEventListener('click', () => {
        this.writeStorage('dismissed', Date.now() + 24 * 3600000);
        this.stopped = true;
        this.hide();
      }, options);
      this.addEventListener('mouseenter', () => { this.hovered = true; }, options);
      this.addEventListener('mouseleave', () => { this.hovered = false; }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.hide();
        else this.schedule(() => this.showNext(), Number(this.dataset.delay));
      }, options);
      window.addEventListener('storage', event => {
        if (event.key === 'fnk-purchases-dismissed' && Number(event.newValue) > Date.now()) {
          this.stopped = true;
          this.hide();
        }
      }, options);
      this.schedule(() => this.showNext(), Number(this.dataset.delay));
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
    }

    readStorage(key) {
      let value = this.storage?.[key] ?? null;
      try { value = sessionStorage.getItem(`fnk-purchases-${key}`) ?? value; } catch {}
      if (key === 'dismissed') {
        try { value = Math.max(Number(value) || 0, Number(localStorage.getItem(`fnk-purchases-${key}`)) || 0); } catch {}
      }
      return value;
    }

    writeStorage(key, value) {
      this.storage ??= {};
      this.storage[key] = String(value);
      try { sessionStorage.setItem(`fnk-purchases-${key}`, String(value)); } catch { /* Storage is optional. */ }
      if (key === 'dismissed') {
        try { localStorage.setItem(`fnk-purchases-${key}`, String(value)); } catch {}
      }
    }

    isRecent(entry) {
      if (entry.dataset.kind === 'announcement') return true;
      const age = Date.now() - Date.parse(entry.dataset.at);
      const maxAge = entry.dataset.source === 'automatic' ? 7 : 30;
      const feedAge = Date.now() - Date.parse(entry.dataset.refreshedAt);
      if (entry.dataset.source === 'automatic' && (!Number.isFinite(feedAge) || feedAge < -60000 || feedAge >= 120000)) return false;
      return Number.isFinite(age) && age >= 0 && age < maxAge * 86400000;
    }

    async loadFeed(signal) {
      try {
        const endpoint = new URL(this.dataset.feed, location.origin);
        if (endpoint.origin !== location.origin || !endpoint.pathname.endsWith('/apps/fnk-tracker/recent-sales') || endpoint.search || endpoint.hash) return [];
        const response = await fetch(endpoint, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.any([signal, AbortSignal.timeout(8000)]) });
        if (!response.ok) return [];
        const feed = await response.json();
        if (feed.version !== 1 || !Array.isArray(feed.entries) || typeof feed.refreshedAt !== 'string') return [];
        return feed.entries.slice(0, 10).map(entry => this.makeEntry(entry, feed.refreshedAt)).filter(Boolean);
      } catch { return []; }
    }

    makeEntry(entry, refreshedAt) {
      const product = entry?.product;
      if (typeof product?.title !== 'string' || !product.title.trim() || !/^\/products\/[a-z0-9][a-z0-9-]*$/.test(product.url || '')) return null;
      let image;
      try { image = new URL(product.image); } catch { return null; }
      if (image.protocol !== 'https:' || image.hostname !== 'cdn.shopify.com' || image.username || image.password) return null;
      const template = document.createElement('template');
      template.dataset.at = entry.purchasedAt;
      template.dataset.source = 'automatic';
      template.dataset.refreshedAt = refreshedAt;
      if (!this.isRecent(template)) return null;
      const link = document.createElement('a');
      link.className = 'fnk-sale-pop__link';
      link.href = (this.dataset.productRoot || '/products/') + product.url.split('/').pop();
      const img = document.createElement('img');
      img.className = 'fnk-sale-pop__image';
      img.src = image.href;
      img.alt = '';
      img.width = 64;
      img.height = 64;
      const body = document.createElement('span');
      body.className = 'fnk-sale-pop__body';
      const label = document.createElement('span');
      label.className = 'fnk-sale-pop__location';
      const name = typeof entry.firstName === 'string' && entry.firstName.trim() ? entry.firstName.slice(0, 40) : this.dataset.someoneLabel;
      const locationLabel = typeof entry.location === 'string' ? entry.location.trim().slice(0, 85) : '';
      const labelPattern = locationLabel ? this.dataset.purchasedFromLabel : this.dataset.purchasedNamedLabel;
      label.textContent = labelPattern ? labelPattern.replace('{name}', () => name).replace('{location}', () => locationLabel) : this.dataset.purchasedLabel;
      const title = document.createElement('span');
      title.className = 'fnk-sale-pop__product';
      title.textContent = product.title.slice(0, 180);
      const time = document.createElement('time');
      time.className = 'fnk-sale-pop__time';
      time.dateTime = entry.purchasedAt;
      body.append(label, title, time);
      link.append(img, body);
      template.content.append(link);
      return template;
    }

    schedule(callback, delay) {
      clearTimeout(this.timer);
      if (!this.stopped && !document.hidden) this.timer = setTimeout(callback, delay);
    }

    showNext() {
      if (this.stopped || this.shown >= this.entries.length || Number(this.readStorage('shown')) >= this.maxSession) return;
      if (Number(this.readStorage('dismissed')) > Date.now()) return;
      const cooldown = Number(this.readStorage('next')) - Date.now();
      if (cooldown > 0) return this.schedule(() => this.showNext(), cooldown);
      const modal = [...document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"]')]
        .some(element => element.getClientRects().length > 0);
      const editing = document.activeElement?.matches('input:not([type="hidden"]), textarea, select, [contenteditable="true"]');
      if (modal || editing) return this.schedule(() => this.showNext(), 3000);

      const entry = this.entries[this.cursor % this.entries.length];
      this.cursor += 1;
      this.shown += 1;
      this.writeStorage('cursor', this.cursor);
      if (!this.isRecent(entry)) return this.schedule(() => this.showNext(), 1000);

      this.writeStorage('shown', (Number(this.readStorage('shown')) || 0) + 1);
      this.gap = Math.round(Number(this.dataset.gap) * (0.5 + Math.random() / 2));
      this.writeStorage('next', Date.now() + 6000 + this.gap);

      this.content.replaceChildren(entry.content.cloneNode(true));
      const time = this.content.querySelector('time');
      if (time) {
      const age = Math.floor((Date.now() - Date.parse(entry.dataset.at)) / 60000);
      const amount = age >= 1440 ? Math.floor(age / 1440) : age >= 60 ? Math.floor(age / 60) : Math.max(1, age);
      const unit = age >= 1440 ? 'day' : age >= 60 ? 'hour' : 'minute';
      time.textContent = this.dataset.timeLabel.replace('{time}', this.timeFormat.format(-amount, unit));
      }
      this.hidden = false;
      this.schedule(() => this.finishCard(), 6000);
    }

    finishCard() {
      if (this.hovered || this.contains(document.activeElement)) return this.schedule(() => this.finishCard(), 1000);
      this.hide();
      this.writeStorage('next', Date.now() + this.gap);
      this.schedule(() => this.showNext(), this.gap);
    }

    hide() {
      clearTimeout(this.timer);
      this.hidden = true;
    }
  });
}
